/**
 * Animated scenery props: a registry of small cached frame loops per prop id (2–8 frames), baked once from the prop's
 * finished sprite and never redrawn per frame. Each frame is the still sprite with a few pixel operations applied on
 * the scene grid (whole-pixel shifts, colour steps within the sprite's own ramps), so outlines and palette hold:
 *
 * - `bend`: rows (axis 'x') or columns (axis 'y') of a region shifted by a whole pixel wave, growing away from a pinned
 *   edge (trees sway from the root, banners and lanterns hang from the top, flags stream from the pole);
 * - `shift`: a region moved rigidly by a small cycle (a gong's swing, a hen's peck);
 * - `flicker`: a flame's or lit paper's own colours stepped up/down in coherent bands (no speckle, no new colours);
 * - `shimmer`: diagonal glints stepping across water, one ramp step brighter;
 * - `smoke`: small grey puffs with a 1-px darker rim rising off a point; `petals`: single petals drifting down.
 *
 * Specs are registered per prop id (kit/propAnimSpecs.ts, kit/propAnim4.ts); passes/kitProps.ts takes the padded
 * frame 0 as the prop's image and kit/propAnimPass.ts picks each placed prop's frame from a global clock with a phase
 * from its position, only while on screen (static while the ambience is off on the low tiers).
 */
import { hash2 } from '@nine/shared';
import { canvas, ctx2d } from '../pixel';
import type { Sprite } from '../scenery';

/** A rectangle in the still sprite's pixels: [x0, y0, x1, y1), x1/y1 exclusive. */
export type Rect = readonly [number, number, number, number];
/** Which pixels of a region an operation takes (default: every opaque one). */
export type Key = 'all' | 'warm' | 'bright' | 'water' | ((r: number, g: number, b: number) => boolean);

export type Op =
  | {
      readonly op: 'bend';
      readonly rect: Rect;
      /** 'x': rows move sideways; 'y': columns move up and down. */
      readonly axis: 'x' | 'y';
      /** The pinned edge, where the shift is 0 (an envelope 'mid' pins both ends of a string). */
      readonly pin: 'top' | 'bottom' | 'left' | 'right' | 'mid' | 'none';
      /** Peak shift (px) at the far edge. */
      readonly amp: number;
      /** Envelope curve (1 linear, 2 tips move most). */
      readonly pow?: number;
      /** A travelling wave's length along the region (px); 0 / absent: the whole region in phase. */
      readonly wave?: number;
      readonly key?: Key;
      /** Rows moving away from their left edge stretch it (a banner stays sewn to its pole: no gap). */
      readonly stretch?: boolean;
      /** Over a backdrop (paper on a board, ribbons on leaves): what a pixel leaves takes the backdrop beside it. */
      readonly backfill?: boolean;
    }
  | {
      readonly op: 'shift';
      readonly rect: Rect;
      readonly path: readonly (readonly [number, number])[];
      readonly key?: Key;
      readonly backfill?: boolean;
    }
  | { readonly op: 'flicker'; readonly rect: Rect; readonly key?: Key; readonly band?: number; readonly cycles?: number }
  | { readonly op: 'shimmer'; readonly rect: Rect; readonly key?: Key; readonly period?: number }
  | { readonly op: 'smoke'; readonly at: readonly [number, number]; readonly rise: number; readonly puffs?: number }
  | { readonly op: 'petals'; readonly rect: Rect; readonly n: number; readonly col: readonly string[] };

export interface AnimSpec {
  /** Frames in the loop (2–8). */
  readonly frames: number;
  /** Seconds per loop. */
  readonly period: number;
  /** Extra transparent room round the sprite for what moves out of it: [left, top, right, bottom]. */
  readonly pad?: readonly [number, number, number, number];
  readonly ops: readonly Op[];
  /** Frame `k` of `n` painted at its source (a wheel's turn), same size and anchor as the still; ops apply on top. */
  readonly make?: (k: number, n: number) => Sprite;
}

const SPECS = new Map<string, AnimSpec>();
/** Registers a prop id's loop (its still stays the prop's own sprite). */
export function registerPropAnim(id: string, spec: AnimSpec): void {
  SPECS.set(id, spec);
}
export const propAnimSpec = (id: string): AnimSpec | undefined => SPECS.get(id);
export const animatedPropIds = (): string[] => [...SPECS.keys()];

/** A prop's baked loop: frames share the padded size and anchor. */
export interface PropLoop extends Sprite {
  readonly frames: readonly HTMLCanvasElement[];
  readonly period: number;
}

const BAKED = new Map<string, PropLoop>();
/** The loop of a prop id, baked once from its still (null when the id has none). */
export function propLoop(id: string, still: Sprite): PropLoop | null {
  const spec = SPECS.get(id);
  if (!spec) return null;
  let l = BAKED.get(id);
  if (!l) BAKED.set(id, (l = bake(still, spec)));
  return l;
}

const lum = (r: number, g: number, b: number): number => r * 0.3 + g * 0.59 + b * 0.11;
function keyFn(k: Key | undefined): (r: number, g: number, b: number) => boolean {
  if (typeof k === 'function') return k;
  switch (k) {
    case 'warm':
      return (r, g, b) => r >= 150 && r > b + 60 && g >= 40;
    case 'bright':
      // Lit paper: light and warm (a pale stone's greys stay out).
      return (r, g, b) => lum(r, g, b) >= 120 && r >= 170 && b < g - 25;
    case 'water':
      return (r, g, b) => b > r + 20 && b >= g - 4;
    default:
      return () => true;
  }
}

/** Bakes every frame of a spec from a still sprite. */
export function bake(still: Sprite, spec: AnimSpec): PropLoop {
  const [pl, pt, pr, pb] = spec.pad ?? [0, 0, 0, 0];
  const w = still.img.width + pl + pr;
  const h = still.img.height + pt + pb;
  const padded = (img: HTMLCanvasElement): Uint8ClampedArray => {
    const out = new Uint8ClampedArray(w * h * 4);
    const sd = ctx2d(img).getImageData(0, 0, img.width, img.height).data;
    for (let y = 0; y < Math.min(img.height, h - pt); y++)
      out.set(sd.subarray(y * img.width * 4, y * img.width * 4 + Math.min(img.width, w - pl) * 4), ((y + pt) * w + pl) * 4);
    return out;
  };
  const src = spec.make ? null : padded(still.img);
  const frames: HTMLCanvasElement[] = [];
  for (let f = 0; f < spec.frames; f++) {
    const d = src ? new Uint8ClampedArray(src) : padded(spec.make!(f, spec.frames).img);
    const ph = f / spec.frames;
    for (const op of spec.ops) apply(op, d, w, h, ph, f, pl, pt);
    const c = canvas(w, h);
    const g = ctx2d(c);
    const img = typeof g.createImageData === 'function' ? g.createImageData(w, h) : new ImageData(w, h);
    img.data.set(d);
    g.putImageData(img, 0, 0);
    frames.push(c);
  }
  // Other fields of the still (a flat decal's footprint) carry over.
  return { ...still, img: frames[0]!, ax: still.ax + pl, ay: still.ay + pt, frames, period: spec.period };
}

function apply(op: Op, d: Uint8ClampedArray, w: number, h: number, ph: number, f: number, ox: number, oy: number): void {
  const R = (r: Rect): [number, number, number, number] => [
    Math.max(0, r[0] + ox),
    Math.max(0, r[1] + oy),
    Math.min(w, r[2] + ox),
    Math.min(h, r[3] + oy),
  ];
  const TAU = Math.PI * 2;
  switch (op.op) {
    case 'bend': {
      const [x0, y0, x1, y1] = R(op.rect);
      const key = keyFn(op.key);
      const along = op.axis === 'x' ? y1 - y0 : x1 - x0;
      const shiftAt = (i: number): number => {
        // `i`: position along the region (row for 'x', column for 'y'), 0 at its top / left.
        const u = along > 1 ? i / (along - 1) : 0;
        const env =
          op.pin === 'top' || op.pin === 'left'
            ? u
            : op.pin === 'bottom' || op.pin === 'right'
              ? 1 - u
              : op.pin === 'mid'
                ? Math.sin(Math.PI * u)
                : 1;
        const wave = op.wave ? Math.sin(TAU * (ph - i / op.wave)) : Math.sin(TAU * ph);
        return Math.round(op.amp * env ** (op.pow ?? 1) * wave);
      };
      const before = op.stretch ? new Uint8ClampedArray(d) : null;
      moveRegion(d, w, h, x0, y0, x1, y1, key, (x, y) => (op.axis === 'x' ? [shiftAt(y - y0), 0] : [0, shiftAt(x - x0)]), op.backfill);
      if (before && op.axis === 'x')
        for (let y = y0; y < y1; y++) {
          const s = shiftAt(y - y0);
          if (s <= 0) continue;
          let e = x0;
          while (e < x1 && !(before[(y * w + e) * 4 + 3]! && key(before[(y * w + e) * 4]!, before[(y * w + e) * 4 + 1]!, before[(y * w + e) * 4 + 2]!))) e++;
          if (e >= x1) continue;
          const j = (y * w + e) * 4;
          for (let k = 0; k < s; k++) {
            const i = (y * w + e + k) * 4;
            if (d[i + 3]) continue;
            d.set(before.subarray(j, j + 4), i);
          }
        }
      return;
    }
    case 'shift': {
      const [x0, y0, x1, y1] = R(op.rect);
      const [dx, dy] = op.path[f % op.path.length]!;
      if (dx || dy) moveRegion(d, w, h, x0, y0, x1, y1, keyFn(op.key), () => [dx, dy], op.backfill);
      return;
    }
    case 'flicker':
    case 'shimmer': {
      const [x0, y0, x1, y1] = R(op.rect);
      const key = keyFn(op.key ?? (op.op === 'flicker' ? 'warm' : 'water'));
      // The region's own ramp: its keyed colours, darkest first.
      const cols = new Map<number, number>();
      for (let y = y0; y < y1; y++)
        for (let x = x0; x < x1; x++) {
          const i = (y * w + x) * 4;
          if (d[i + 3]! < 128 || !key(d[i]!, d[i + 1]!, d[i + 2]!)) continue;
          cols.set((d[i]! << 16) | (d[i + 1]! << 8) | d[i + 2]!, lum(d[i]!, d[i + 1]!, d[i + 2]!));
        }
      const ramp = [...cols.entries()].sort((a, b) => a[1] - b[1]).map((e) => e[0]);
      if (ramp.length < 2) return;
      const rank = new Map(ramp.map((c, k) => [c, k]));
      const out = new Uint8ClampedArray(d);
      for (let y = y0; y < y1; y++)
        for (let x = x0; x < x1; x++) {
          const i = (y * w + x) * 4;
          if (d[i + 3]! < 128) continue;
          const k = rank.get((d[i]! << 16) | (d[i + 1]! << 8) | d[i + 2]!);
          if (k === undefined) continue;
          let step: number;
          if (op.op === 'flicker') {
            // Coherent bands rising through the flame: a whole cluster steps together.
            const b = op.band ?? 3;
            const v = Math.sin(TAU * ph * (op.cycles ?? 1) + (y - y1) / b + Math.floor((x - x0) / 3) * 1.7);
            step = v > 0.55 ? 1 : v < -0.55 ? -1 : 0;
          } else {
            const p = op.period ?? 11;
            const s = (((x + 2 * y - Math.round(ph * p)) % p) + p) % p;
            step = s === 0 && hash2(Math.floor((x + 2 * y) / p), y >> 2) > 0.35 ? 1 : 0;
          }
          const c = ramp[Math.max(0, Math.min(ramp.length - 1, k + step))]!;
          out[i] = c >> 16;
          out[i + 1] = (c >> 8) & 255;
          out[i + 2] = c & 255;
        }
      d.set(out);
      return;
    }
    case 'smoke': {
      const [sx, sy] = [op.at[0] + ox, op.at[1] + oy];
      const puffs = op.puffs ?? 3;
      for (let k = 0; k < puffs; k++) {
        const u = (ph + k / puffs) % 1;
        const cx = sx + Math.round(Math.sin(u * Math.PI * 1.3 + k) * 1.5 + u * 3);
        const cy = sy - Math.round(u * op.rise);
        puff(d, w, h, cx - 1, cy, u);
      }
      return;
    }
    case 'petals': {
      const [x0, y0, x1, y1] = R(op.rect);
      for (let k = 0; k < op.n; k++) {
        const u = (ph + hash2(k, 11)) % 1;
        const x = Math.round(x0 + hash2(k, 5) * (x1 - x0 - 1) + Math.sin(TAU * u + k) * 2);
        const y = Math.round(y0 + u * (y1 - y0 - 1));
        if (x < 0 || x >= w || y < 0 || y >= h) continue;
        const i = (y * w + x) * 4;
        if (d[i + 3]) continue;
        const c = op.col[(k + f) % op.col.length]!;
        d[i] = parseInt(c.slice(1, 3), 16);
        d[i + 1] = parseInt(c.slice(3, 5), 16);
        d[i + 2] = parseInt(c.slice(5, 7), 16);
        d[i + 3] = 255;
      }
      return;
    }
  }
}

/** Grey ramp of the smoke: rim, shade, body, light. */
const SMOKE: readonly (readonly [number, number, number])[] = [
  [70, 66, 72],
  [128, 124, 124],
  [176, 172, 166],
  [212, 208, 200],
];
/** A puff `u` (0–1) of the way up: a dot, then a 2×2 and a 3×3 body in a 1-px darker rim (round corners), then gone thin. */
function puff(d: Uint8ClampedArray, w: number, h: number, cx: number, cy: number, u: number): void {
  const set = (x: number, y: number, c: readonly [number, number, number]) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const i = (y * w + x) * 4;
    if (d[i + 3]) return;
    d[i] = c[0];
    d[i + 1] = c[1];
    d[i + 2] = c[2];
    d[i + 3] = 255;
  };
  if (u < 0.18) return set(cx, cy, SMOKE[2]!);
  if (u > 0.86) {
    set(cx, cy, SMOKE[1]!);
    set(cx + 1, cy - 1, SMOKE[1]!);
    return;
  }
  // A soft lump: wider than tall, lit on top, shade below, the dark 1-px rim only on its lower and right edges.
  const n = u < 0.45 ? 2 : u < 0.7 ? 3 : 4;
  const m = Math.max(2, n - 1);
  for (let y = 0; y < m; y++)
    for (let x = 0; x < n; x++) {
      if ((x === 0 || x === n - 1) && (y === 0 || y === m - 1) && n > 2) continue;
      set(cx + x, cy + y, y === 0 ? SMOKE[3]! : y === m - 1 ? SMOKE[1]! : SMOKE[2]!);
    }
  for (let x = n > 2 ? 1 : 0; x < (n > 2 ? n - 1 : n); x++) set(cx + x, cy + m, SMOKE[0]!);
  set(cx + n, cy + Math.floor(m / 2), SMOKE[0]!);
}

/** Moves the keyed pixels of a region by a per-pixel offset; what they leave goes transparent. */
function moveRegion(
  d: Uint8ClampedArray,
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  key: (r: number, g: number, b: number) => boolean,
  off: (x: number, y: number) => readonly [number, number],
  backfill = false,
): void {
  const orig = backfill ? new Uint8ClampedArray(d) : null;
  const left: [number, number, number, number][] = [];
  const taken: [number, number, number, number, number, number][] = [];
  for (let y = y0; y < y1; y++)
    for (let x = x0; x < x1; x++) {
      const i = (y * w + x) * 4;
      if (!d[i + 3] || !key(d[i]!, d[i + 1]!, d[i + 2]!)) continue;
      const [dx, dy] = off(x, y);
      if (!dx && !dy) continue;
      taken.push([x + dx, y + dy, d[i]!, d[i + 1]!, d[i + 2]!, d[i + 3]!]);
      d[i + 3] = 0;
      if (orig) left.push([x, y, x - Math.sign(dx), y - Math.sign(dy)]);
    }
  for (const [x, y, r, g, b, a] of taken) {
    if (x < 0 || y < 0 || x >= w || y >= h) continue;
    const i = (y * w + x) * 4;
    d[i] = r;
    d[i + 1] = g;
    d[i + 2] = b;
    d[i + 3] = a;
  }
  // A left pixel nothing moved into takes the still pixel behind it (the backdrop the piece slid off).
  if (orig)
    for (const [x, y, bx, by] of left) {
      const i = (y * w + x) * 4;
      if (d[i + 3] || bx < 0 || by < 0 || bx >= w || by >= h) continue;
      const j = (by * w + bx) * 4;
      if (!orig[j + 3] || !d[j + 3]) continue;
      d.set(d.subarray(j, j + 4), i);
    }
}
